import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Play, Square, MapPin, ClipboardCheck, LogOut, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StaffAttendanceCard — clock in/out with GPS + auto-attendance.
 * Uses the existing driver-shifts endpoints (now also open to internal_staff).
 */
export default function StaffAttendanceCard() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [vehicleReg, setVehicleReg] = useState('');
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, hist] = await Promise.all([
        axios.get(`${API_URL}/api/driver-shifts/current`, { headers }),
        axios.get(`${API_URL}/api/driver-shifts/my`, { headers }),
      ]);
      setCurrent(cur.data || null);
      setHistory(hist.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null, accuracy: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({ lat: null, lng: null, accuracy: null }),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  });

  const clockIn = async () => {
    if (!vehicleReg.trim()) return toast.error('Vehicle registration is required');
    const km = Number(startKm);
    if (!km || km <= 0) return toast.error('Enter the current odometer (start KM) — required');
    setBusy(true);
    try {
      const geo = await getGeo();
      await axios.post(`${API_URL}/api/driver-shifts/start`, {
        ...geo,
        vehicle_registration: vehicleReg.trim(),
        start_km: km,
      }, { headers });
      toast.success('Clocked in — GPS captured');
      setStartKm('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clock in failed');
    } finally { setBusy(false); }
  };

  const markAttendance = async () => {
    if (!current?.id) return;
    setBusy(true);
    try {
      const geo = await getGeo();
      await axios.post(`${API_URL}/api/driver-shifts/${current.id}/step`, {
        step: 'attendance',
        data: { ...geo, notes: 'Marked via staff attendance tab' },
      }, { headers });
      toast.success('Attendance marked (P) on your register');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const clockOff = async () => {
    if (!current?.id) return;
    const km = Number(endKm);
    if (!km || km <= 0) return toast.error('Enter the closing odometer (end KM) — required');
    const startKmVal = Number(current?.start_km || 0);
    if (startKmVal && km < startKmVal) return toast.error(`End reading can't be less than start (${startKmVal} km)`);
    if (!window.confirm('Clock off now? This ends your shift and captures your final GPS position + odometer.')) return;
    setBusy(true);
    try {
      const geo = await getGeo();
      await axios.post(`${API_URL}/api/driver-shifts/${current.id}/step`, {
        step: 'off_duty',
        data: { ...geo, end_km: km },
      }, { headers });
      toast.success('Clocked off — shift completed');
      setEndKm('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const attendanceMarked = !!(current?.steps?.attendance);
  const onDutyTs = current?.steps?.on_duty?.ts;

  return (
    <Card data-testid="staff-attendance-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-emerald-600" /> Clock In / Out
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={fetchAll} disabled={loading}>
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-6 text-muted-foreground">Loading shift…</div>
        ) : current ? (
          <>
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 space-y-2" data-testid="active-shift">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase text-emerald-700 tracking-wider">Active Shift</div>
                  <div className="text-xs text-emerald-900">Started {onDutyTs ? new Date(onDutyTs).toLocaleTimeString() : '—'}</div>
                </div>
                <Badge className="bg-emerald-600">IN PROGRESS</Badge>
              </div>
              <div className="text-xs text-emerald-900 flex flex-wrap gap-3">
                {current.vehicle_registration && (
                  <span><b>Vehicle:</b> {current.vehicle_registration}</span>
                )}
                {current.start_km ? (
                  <span><b>Start KM:</b> {Number(current.start_km).toLocaleString('en-ZA')}</span>
                ) : null}
                {current.steps?.on_duty?.lat != null && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{current.steps.on_duty.lat.toFixed(4)}, {current.steps.on_duty.lng.toFixed(4)}</span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Odometer End (KM) *</label>
              <Input
                type="number"
                inputMode="numeric"
                min={current.start_km || 1}
                value={endKm}
                onChange={(e) => setEndKm(e.target.value)}
                placeholder="Reading at end of shift"
                data-testid="clock-off-end-km"
              />
              {Number(endKm) > 0 && Number(current.start_km || 0) > 0 && Number(endKm) >= Number(current.start_km) && (
                <p className="text-[11px] text-emerald-700 mt-1">Distance today: <b>{(Number(endKm) - Number(current.start_km)).toLocaleString('en-ZA')} km</b></p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={markAttendance}
                disabled={busy || attendanceMarked}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="mark-attendance-btn"
              >
                <ClipboardCheck className="h-4 w-4 mr-2" />
                {attendanceMarked ? 'Attendance ✓' : 'Mark Attendance (P)'}
              </Button>
              <Button
                onClick={clockOff}
                disabled={busy || !endKm}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="clock-off-btn"
              >
                <LogOut className="h-4 w-4 mr-2" /> Clock Off
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Vehicle Registration *</label>
              <Input
                value={vehicleReg}
                onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                placeholder="e.g. CFM94394"
                data-testid="vehicle-reg-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Odometer Start (KM) *</label>
              <Input
                type="number"
                inputMode="numeric"
                min="1"
                value={startKm}
                onChange={(e) => setStartKm(e.target.value)}
                placeholder="Current km on the vehicle"
                data-testid="clock-in-start-km"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Required — this reading is pushed to Fleet Maintenance so service scheduling stays accurate.</p>
            </div>
            <Button
              onClick={clockIn}
              disabled={busy || !vehicleReg.trim() || !startKm}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              data-testid="clock-in-btn"
            >
              <Play className="h-5 w-5 mr-2" /> Clock In (Capture GPS)
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">
              Your device may ask for location permission. GPS is required so admin sees where you started.
            </p>
          </div>
        )}

        {history.length > 0 && (
          <div className="pt-2">
            <div className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Recent shifts</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>In</TableHead>
                    <TableHead>Out</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Start KM</TableHead>
                    <TableHead className="text-right">End KM</TableHead>
                    <TableHead className="text-right">Distance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.slice(0, 5).map((s) => (
                    <TableRow key={s.id} data-testid={`shift-history-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.shift_date}</TableCell>
                      <TableCell className="text-xs">{s.started_at ? new Date(s.started_at).toLocaleTimeString() : '—'}</TableCell>
                      <TableCell className="text-xs">{s.ended_at ? new Date(s.ended_at).toLocaleTimeString() : '—'}</TableCell>
                      <TableCell className="text-xs">{s.vehicle_registration || '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.start_km ? Number(s.start_km).toLocaleString('en-ZA') : '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{s.end_km ? Number(s.end_km).toLocaleString('en-ZA') : '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono font-semibold text-emerald-700">{s.distance_km ? Number(s.distance_km).toLocaleString('en-ZA') : '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={s.status === 'completed' ? 'bg-slate-100' : 'bg-emerald-100 text-emerald-800'}>
                          {s.status || 'in_progress'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
